import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';

/**
 * The search box and filter row shared by the list pages (bills, cases,
 * steerages, payments): one box for identifiers, dropdowns for categories,
 * date presets, a result count and a way back to the defaults.
 */

export type DateRange = { fromDate?: Date; untilDate?: Date };

export type DateOption = {
  value: string;
  label: string;
  /** The dates this choice stands for, worked out when it is picked. */
  range?: () => DateRange;
};

export type SelectFilter = {
  key: string;
  id: string;
  label: string;
  options: { value: string; label: string }[];
};

export type ListFilters = {
  search: string;
  datePreset: string;
  fromDate?: Date;
  untilDate?: Date;
  [select: string]: string | Date | undefined;
};

const startOfDay = (d: Date) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};
const endOfDay = (d: Date) => {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
};
const addDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};

/** Looking back: when something happened. */
export const PAST_DATE_OPTIONS: DateOption[] = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days', range: () => ({ fromDate: startOfDay(addDays(-7)), untilDate: new Date() }) },
  { value: '30d', label: 'Last 30 days', range: () => ({ fromDate: startOfDay(addDays(-30)), untilDate: new Date() }) },
  {
    value: 'month',
    label: 'This month',
    range: () => {
      const now = new Date();
      return { fromDate: new Date(now.getFullYear(), now.getMonth(), 1), untilDate: now };
    },
  },
  { value: 'custom', label: 'Custom range' },
];

/** Looking ahead: when something is due. */
export const DUE_DATE_OPTIONS: DateOption[] = [
  { value: 'all', label: 'Any due date' },
  { value: 'overdue', label: 'Overdue', range: () => ({ untilDate: new Date() }) },
  { value: 'next7', label: 'Due in next 7 days', range: () => ({ fromDate: new Date(), untilDate: endOfDay(addDays(7)) }) },
  {
    value: 'month',
    label: 'Due this month',
    range: () => {
      const now = new Date();
      return { fromDate: new Date(now.getFullYear(), now.getMonth(), 1), untilDate: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
    },
  },
  { value: 'custom', label: 'Custom range' },
];

/** Whether anything differs from the page's defaults (dates follow the preset). */
export const isDefaultFilters = (f: ListFilters, defaults: ListFilters) =>
  Object.keys(defaults)
    .filter((k) => k !== 'fromDate' && k !== 'untilDate')
    .every((k) => f[k] === defaults[k]);

const toInputDate = (date?: Date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  search: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: theme.palette.background.paper,
  },
  select: {
    minWidth: 160,
    backgroundColor: theme.palette.background.paper,
  },
  date: {
    width: 170,
    backgroundColor: theme.palette.background.paper,
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

type Props<F extends ListFilters> = {
  filters: F;
  defaults: F;
  onChange: (next: F) => void;
  total: number;
  /** What the count is of, e.g. "bills". */
  noun: string;
  searchPlaceholder: string;
  selects: SelectFilter[];
  dateLabel: string;
  dateOptions: DateOption[];
  /** Select keys the page has pinned, e.g. status during bulk approval. */
  locked?: Partial<Record<string, boolean>>;
  /** The tenant picker, which only super users get. */
  tenantSlot?: React.ReactNode;
};

export function ListFilterBar<F extends ListFilters>({
  filters,
  defaults,
  onChange,
  total,
  noun,
  searchPlaceholder,
  selects,
  dateLabel,
  dateOptions,
  locked = {},
  tenantSlot,
}: Props<F>) {
  const classes = useStyles();
  const { t } = useTranslation();

  // The box is typed into freely; the page is only asked to reload once
  // typing pauses, so an ID does not fire a request per keystroke.
  const [searchText, setSearchText] = useState(filters.search);
  const latest = useRef(filters);
  latest.current = filters;

  useEffect(() => {
    setSearchText(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed === latest.current.search) return;
    const timer = setTimeout(() => onChange({ ...latest.current, search: trimmed }), 400);
    return () => clearTimeout(timer);
  }, [searchText, onChange]);

  const set = (patch: Partial<ListFilters>) => onChange({ ...filters, ...patch } as F);

  const handleDatePreset = (value: string) => {
    const option = dateOptions.find((o) => o.value === value);
    if (value === 'custom') {
      set({ datePreset: value });
    } else {
      // Spelled out so "all" clears the dates a previous preset set.
      const { fromDate, untilDate } = option?.range?.() ?? {};
      set({ datePreset: value, fromDate, untilDate });
    }
  };

  const parseInputDate = (value: string) => (value ? new Date(value) : undefined);

  const clear = () => {
    setSearchText('');
    const pinned = Object.keys(locked).filter((k) => locked[k]);
    const next = { ...defaults, fromDate: undefined, untilDate: undefined } as F;
    pinned.forEach((k) => ((next as ListFilters)[k] = filters[k]));
    onChange(next);
  };

  return (
    <Box className={classes.root}>
      <TextField
        className={classes.search}
        variant="outlined"
        size="small"
        placeholder={t(searchPlaceholder)}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        inputProps={{ 'aria-label': t(`Search ${noun}`) }}
      />

      <Box className={classes.row}>
        {selects.map((select) => (
          <TextField
            key={select.key}
            select
            id={select.id}
            className={classes.select}
            variant="outlined"
            size="small"
            label={t(select.label)}
            value={filters[select.key] ?? ''}
            onChange={(e) => set({ [select.key]: e.target.value as string })}
            disabled={!!locked[select.key]}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            {select.options.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </TextField>
        ))}

        <TextField
          select
          id="list-date-preset"
          className={classes.select}
          variant="outlined"
          size="small"
          label={t(dateLabel)}
          value={filters.datePreset}
          onChange={(e) => handleDatePreset(e.target.value as string)}
          InputLabelProps={{ shrink: true }}
        >
          {dateOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {t(o.label)}
            </MenuItem>
          ))}
        </TextField>

        {filters.datePreset === 'custom' && (
          <>
            <TextField
              id="list-date-from"
              className={classes.date}
              type="date"
              variant="outlined"
              size="small"
              label={t('From')}
              value={toInputDate(filters.fromDate)}
              onChange={(e) => set({ fromDate: parseInputDate(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toInputDate(filters.untilDate) || undefined }}
            />
            <TextField
              id="list-date-until"
              className={classes.date}
              type="date"
              variant="outlined"
              size="small"
              label={t('Until')}
              value={toInputDate(filters.untilDate)}
              onChange={(e) => set({ untilDate: parseInputDate(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: toInputDate(filters.fromDate) || undefined }}
            />
          </>
        )}

        {tenantSlot}
      </Box>

      <Box className={classes.summary}>
        <Typography variant="body2">{`${total} ${t(noun)}`}</Typography>
        {!isDefaultFilters(filters, defaults) && (
          <>
            <Typography variant="body2">·</Typography>
            <Link component="button" variant="body2" onClick={clear}>
              {t('Clear filters')}
            </Link>
          </>
        )}
      </Box>
    </Box>
  );
}

export default ListFilterBar;
